import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SORTING_HEARTBEAT_SECONDS,
  SORTING_LEASE_MINUTES,
  SORTING_WINDOW,
  UNDO_DEPTH,
  type PoolOption,
  type SortingAction,
  type SortingCard,
  type SortingCounters,
  type SortingProgress,
  type SortingSessionView,
  type SortingStartResponse,
  type SortingSummary,
  type SortOrder,
} from '@nest-recruit/shared';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import {
  changeSortOrder,
  endSession,
  endSessionBeacon,
  fetchCards,
  postDecision,
  postHeartbeat,
  postNote,
  postUndo,
} from '../api/sorting-api';
import {
  decisionRequestOf,
  displayCounters,
  displayProgress,
  heldIds,
  isRetryable,
  mergeCards,
  type LocalDecision,
  type PendingOp,
} from '../sorting-state';

/** §L26 "Save failed". Shown once per offline spell, not once per decision. */
const SAVE_FAILED = "Couldn't save. Retrying — your work is kept.";
const RETRY_MS = 5_000;
const LEASE_MS = SORTING_LEASE_MINUTES * 60_000;
/** How long leaving waits for unsent decisions before ending the session anyway. */
const FINISH_WAIT_MS = 15_000;
const SORTING_ENDED = 'sorting_ended';

interface NoteState {
  readonly text: string;
  readonly activityId: string | null;
  readonly isDirty: boolean;
}

interface State {
  readonly cards: readonly SortingCard[];
  readonly session: SortingSessionView;
  readonly progress: SortingProgress;
  readonly pools: readonly PoolOption[];
  readonly pending: readonly PendingOp[];
  readonly decisions: readonly LocalDecision[];
  readonly notes: Readonly<Record<string, NoteState>>;
  readonly isOffline: boolean;
  readonly mayRefill: boolean;
  readonly isRefilling: boolean;
  /** SCR-01 state i: why sorting stopped, when it did. */
  readonly endedMessage: string | null;
  /** When the card on screen appeared — its dwell time is measured from here (F8.02). */
  readonly shownAt: number;
}

export type SortingStatus = 'active' | 'loading' | 'exhausted' | 'ended';

export interface RejectChoice {
  readonly reasonCode: string;
  readonly poolIds: readonly string[];
  readonly newPoolNames: readonly string[];
  /** ADR 0046: "Also add to CV Bank", ticked in the reject box. */
  readonly addToCvBank: boolean;
}

export interface SortingSession {
  readonly status: SortingStatus;
  readonly current: SortingCard | null;
  readonly upcoming: readonly SortingCard[];
  readonly counters: SortingCounters;
  readonly position: number;
  readonly total: number;
  readonly sortOrder: SortOrder;
  readonly pools: readonly PoolOption[];
  readonly pendingCount: number;
  readonly isOffline: boolean;
  readonly endedMessage: string | null;
  readonly canUndo: boolean;
  readonly startedAt: string;
  readonly note: string;
  readonly isNoteUnsaved: boolean;
  decide(action: SortingAction, choice?: RejectChoice): void;
  undo(): void;
  setNote(text: string): void;
  saveNote(): void;
  changeSort(order: SortOrder): void;
  /** Leaves: sends what is waiting, then ends the session in the background. */
  finish(): Promise<SortingSummary | null>;
}

/**
 * SCR-01's queue, kept on the page. A decision takes the card off the screen the
 * moment the key is pressed and is sent afterwards, in order, from a small outbox
 * (non-negotiable #11); a dropped connection keeps it and retries (state h). The
 * next three cards are always already here (F8.02), so moving on costs nothing.
 */
export function useSortingSession(start: SortingStartResponse, session: SortingSessionView): SortingSession {
  const sessionId = session.id;
  const [state, setState] = useState<State>(() => initialState(start, session));
  const ref = useRef<State>(state);
  const flushing = useRef(false);
  const inFlight = useRef<PendingOp | null>(null);
  const refilling = useRef(false);
  const retryTimer = useRef<number | null>(null);
  const decisionIds = useRef(new Map<string, string>());
  const lastActivity = useRef(0);
  const finished = useRef<Promise<SortingSummary | null> | null>(null);
  // A refill that dropped skipped cards asks again; through a ref, since a callback cannot name itself.
  const refillAgain = useRef<() => Promise<void>>(() => Promise.resolve());

  // The ref is the truth async code reads; state only asks React to draw it.
  const update = useCallback((change: (current: State) => State) => {
    ref.current = change(ref.current);
    setState(ref.current);
  }, []);

  const toast = useToasts.getState();

  // --- Sending --------------------------------------------------------------------------

  const endWith = useCallback(
    (error: unknown): boolean => {
      if (!(error instanceof ApiError) || error.code !== SORTING_ENDED) return false;
      update((current) => ({ ...current, endedMessage: error.message }));
      return true;
    },
    [update],
  );

  const send = useCallback(
    async (op: PendingOp): Promise<void> => {
      const current = ref.current;
      if (op.kind === 'decision') {
        const note = current.notes[op.card.applicationId];
        const base = {
          clientDecisionId: op.clientDecisionId,
          applicationId: op.card.applicationId,
          ...(note && note.text.trim() !== '' ? { note: note.text } : {}),
          noteActivityId: note?.activityId ?? null,
          dwellMs: op.dwellMs,
        };
        const response = await postDecision(sessionId, decisionRequestOf(op, base));
        const recorded = response.session.recent.find((decision) => decision.applicationId === op.card.applicationId);
        if (recorded) decisionIds.current.set(op.clientDecisionId, recorded.decisionId);
        update((state) => ({
          ...state,
          session: response.session,
          progress: response.progress,
          pools: [...state.pools, ...response.createdPools.filter((pool) => !state.pools.some((known) => known.id === pool.id))],
          notes: note ? { ...state.notes, [op.card.applicationId]: { ...note, activityId: response.noteActivityId, isDirty: false } } : state.notes,
          decisions: state.decisions.map((decision) =>
            decision.clientDecisionId === op.clientDecisionId
              ? { ...decision, decisionId: recorded?.decisionId ?? null, cannotUndoReason: recorded?.cannotUndoReason ?? null }
              : decision,
          ),
        }));
        return;
      }
      if (op.kind === 'undo') {
        // Queued before its decision was recorded, it carries the browser's id; by
        // now the decision has gone (the outbox is in order) and its real id is known.
        const decisionId = decisionIds.current.get(op.decisionId) ?? op.decisionId;
        const response = await postUndo(sessionId, decisionId);
        update((state) => ({
          ...state,
          session: response.session,
          progress: response.progress,
          cards: state.cards.map((card) => (card.applicationId === response.card.applicationId ? response.card : card)),
        }));
        return;
      }
      const note = current.notes[op.applicationId];
      if (!note || (note.text.trim() === '' && note.activityId === null)) return;
      const saved = await postNote(sessionId, { applicationId: op.applicationId, note: note.text, activityId: note.activityId });
      update((state) => {
        const latest = state.notes[op.applicationId];
        if (!latest) return state;
        return { ...state, notes: { ...state.notes, [op.applicationId]: { ...latest, activityId: saved.activityId, isDirty: latest.text !== note.text } } };
      });
    },
    [sessionId, update],
  );

  /** The server said no: undo the optimistic step and say why (§5a rule 1). */
  const refused = useCallback(
    (op: PendingOp, error: unknown): void => {
      if (endWith(error)) return;
      const message = errorMessage(error);
      const isFixable = error instanceof ApiError && error.status === 422;
      update((state) => {
        const next = { ...state };
        if (op.kind === 'decision') {
          next.decisions = state.decisions.filter((decision) => decision.clientDecisionId !== op.clientDecisionId);
          // A reason that cannot be used (three contact attempts, a retired reason):
          // the card comes back so the recruiter can choose again.
          if (isFixable) next.cards = [op.card, ...state.cards];
        }
        if (op.kind === 'undo') next.cards = state.cards.filter((card) => card.applicationId !== op.card.applicationId);
        return { ...next, shownAt: Date.now() };
      });
      const lead = op.kind === 'decision' ? `${op.card.candidate.fullName}: not saved` : op.kind === 'undo' ? "That decision wasn't undone" : 'Note not saved';
      toast.show({ tone: 'danger', message: lead, detail: message });
    },
    [endWith, toast, update],
  );

  const refill = useCallback(async (): Promise<void> => {
    const current = ref.current;
    const need = SORTING_WINDOW - current.cards.length;
    if (need <= 0 || refilling.current || current.endedMessage !== null) return;
    if (!current.mayRefill && current.cards.length > 0) return;
    refilling.current = true;
    update((state) => ({ ...state, isRefilling: true }));
    try {
      const holding = current.cards.map((card) => card.applicationId);
      const response = await fetchCards(sessionId, { count: need, holding, exclude: [...heldIds([], current.pending)] });
      const skipped = new Set(response.skipped.map((card) => card.applicationId));
      for (const card of response.skipped) toast.show({ tone: 'warning', message: card.message, key: `skip-${card.applicationId}` });
      update((state) => {
        const leaving = new Set(heldIds([], state.pending));
        const kept = state.cards.filter((card) => !skipped.has(card.applicationId));
        return {
          ...state,
          cards: mergeCards(kept, response.cards, leaving),
          progress: response.progress,
          mayRefill: response.cards.length >= need,
          isOffline: false,
          shownAt: kept[0]?.applicationId === state.cards[0]?.applicationId ? state.shownAt : Date.now(),
        };
      });
      refilling.current = false;
      if (skipped.size > 0) void refillAgain.current();
    } catch (error) {
      refilling.current = false;
      if (!endWith(error)) {
        if (isRetryable(error)) update((state) => ({ ...state, isOffline: true }));
        else toast.show({ tone: 'danger', message: "The next applications didn't load", detail: errorMessage(error) });
      }
    } finally {
      update((state) => ({ ...state, isRefilling: false }));
    }
  }, [endWith, sessionId, toast, update]);

  useEffect(() => {
    refillAgain.current = refill;
  }, [refill]);

  const flush = useCallback(async (): Promise<void> => {
    if (flushing.current) return;
    flushing.current = true;
    try {
      while (ref.current.pending.length > 0 && ref.current.endedMessage === null) {
        const op = ref.current.pending[0];
        if (!op) break;
        inFlight.current = op;
        try {
          await send(op);
          update((state) => ({ ...state, pending: state.pending.slice(1), isOffline: false }));
        } catch (error) {
          if (isRetryable(error)) {
            if (!ref.current.isOffline) toast.show({ tone: 'warning', message: SAVE_FAILED, key: 'sorting-offline' });
            update((state) => ({ ...state, isOffline: true }));
            retryTimer.current ??= window.setTimeout(() => {
              retryTimer.current = null;
              void flush();
            }, RETRY_MS);
            return;
          }
          update((state) => ({ ...state, pending: state.pending.slice(1) }));
          refused(op, error);
        } finally {
          inFlight.current = null;
        }
      }
    } finally {
      flushing.current = false;
    }
    void refill();
  }, [refill, refused, send, toast, update]);

  // --- Actions -----------------------------------------------------------------------------

  const decide = useCallback(
    (action: SortingAction, choice?: RejectChoice): void => {
      const current = ref.current;
      const card = current.cards[0];
      if (!card || current.endedMessage !== null) return;
      const now = Date.now();
      const op: PendingOp = {
        kind: 'decision',
        clientDecisionId: crypto.randomUUID(),
        action,
        card,
        reasonCode: choice?.reasonCode ?? null,
        poolIds: choice?.poolIds ?? [],
        newPoolNames: choice?.newPoolNames ?? [],
        addToCvBank: choice?.addToCvBank ?? false,
        dwellMs: now - current.shownAt,
      };
      update((state) => ({
        ...state,
        cards: state.cards.slice(1),
        pending: [...state.pending, op],
        decisions: [{ clientDecisionId: op.clientDecisionId, decisionId: null, action, card, cannotUndoReason: null }, ...state.decisions].slice(0, UNDO_DEPTH),
        mayRefill: true,
        shownAt: now,
      }));
      void flush();
      void refill();
    },
    [flush, refill, update],
  );

  const undo = useCallback((): void => {
    const current = ref.current;
    const last = current.decisions[0];
    if (!last || current.endedMessage !== null) return;
    if (last.cannotUndoReason) {
      toast.show({ tone: 'info', message: `${last.card.candidate.fullName}: can't undo`, detail: last.cannotUndoReason });
      return;
    }
    const queued = current.pending.find((op) => op.kind === 'decision' && op.clientDecisionId === last.clientDecisionId);
    const isUnsent = queued !== undefined && inFlight.current !== queued;
    update((state) => ({
      ...state,
      cards: [last.card, ...state.cards.filter((card) => card.applicationId !== last.card.applicationId)],
      decisions: state.decisions.slice(1),
      // Never sent: nothing happened on the server, so there is nothing to log.
      pending: isUnsent
        ? state.pending.filter((op) => op !== queued)
        : [...state.pending, { kind: 'undo', id: crypto.randomUUID(), decisionId: last.decisionId ?? last.clientDecisionId, card: last.card, action: last.action }],
      shownAt: Date.now(),
    }));
    void flush();
  }, [flush, toast, update]);

  const setNote = useCallback(
    (text: string): void => {
      const card = ref.current.cards[0];
      if (!card) return;
      update((state) => {
        const previous = state.notes[card.applicationId];
        return { ...state, notes: { ...state.notes, [card.applicationId]: { text, activityId: previous?.activityId ?? null, isDirty: true } } };
      });
    },
    [update],
  );

  /** F8.08 "autosaves on blur". */
  const saveNote = useCallback((): void => {
    const card = ref.current.cards[0];
    if (!card || !ref.current.notes[card.applicationId]?.isDirty) return;
    update((state) => ({ ...state, pending: [...state.pending, { kind: 'note', id: crypto.randomUUID(), applicationId: card.applicationId }] }));
    void flush();
  }, [flush, update]);

  const changeSort = useCallback(
    (order: SortOrder): void => {
      const current = ref.current;
      if (order === current.session.sortOrder) return;
      const keep = current.cards[0] ? [current.cards[0].applicationId] : [];
      update((state) => ({ ...state, session: { ...state.session, sortOrder: order }, cards: state.cards.slice(0, keep.length), mayRefill: true }));
      changeSortOrder(sessionId, order, keep)
        .then((response) => update((state) => ({ ...state, cards: mergeCards(state.cards, response.cards, new Set(heldIds([], state.pending))), progress: response.progress })))
        .catch((error: unknown) => {
          if (!endWith(error)) toast.show({ tone: 'danger', message: "The order didn't change", detail: errorMessage(error) });
          void refill();
        });
    },
    [endWith, refill, sessionId, toast, update],
  );

  const finish = useCallback((): Promise<SortingSummary | null> => {
    finished.current ??= (async () => {
      const deadline = Date.now() + FINISH_WAIT_MS;
      void flush();
      while (ref.current.pending.length > 0 && ref.current.endedMessage === null && Date.now() < deadline) {
        await new Promise((resolve) => window.setTimeout(resolve, 250));
      }
      if (ref.current.endedMessage !== null) return null;
      const card = ref.current.cards[0];
      try {
        return await endSession(sessionId, card ? { applicationId: card.applicationId, dwellMs: Date.now() - ref.current.shownAt } : {});
      } catch {
        // The session resumes next time and lapses after twelve hours; nothing is lost.
        return null;
      }
    })();
    return finished.current;
  }, [flush, sessionId]);

  // --- Background: retry on reconnect, keep the leases, end on tab close -------------------

  useEffect(() => {
    const onOnline = (): void => {
      void flush();
      void refill();
    };
    const onActivity = (): void => {
      lastActivity.current = Date.now();
    };
    lastActivity.current = Date.now();
    const onPageHide = (): void => {
      if (!finished.current) endSessionBeacon(sessionId, {});
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('keydown', onActivity, true);
    window.addEventListener('pointerdown', onActivity, true);
    window.addEventListener('pagehide', onPageHide);
    // SCR-01 §13: five idle minutes free a card. Working means renewing.
    const heartbeat = window.setInterval(() => {
      if (Date.now() - lastActivity.current > LEASE_MS || ref.current.endedMessage !== null) return;
      postHeartbeat(sessionId, heldIds(ref.current.cards, [])).catch(() => undefined);
    }, SORTING_HEARTBEAT_SECONDS * 1000);
    void refill();
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('keydown', onActivity, true);
      window.removeEventListener('pointerdown', onActivity, true);
      window.removeEventListener('pagehide', onPageHide);
      window.clearInterval(heartbeat);
      if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
    };
  }, [flush, refill, sessionId]);

  const card = state.cards[0] ?? null;
  const note = card ? state.notes[card.applicationId] : undefined;
  const { position, total } = displayProgress(state.progress, state.pending);

  return {
    status: statusOf(state),
    current: card,
    upcoming: state.cards.slice(1),
    counters: displayCounters(state.session.counters, state.pending),
    position,
    total,
    sortOrder: state.session.sortOrder,
    pools: state.pools,
    pendingCount: state.pending.filter((op) => op.kind !== 'note').length,
    isOffline: state.isOffline,
    endedMessage: state.endedMessage,
    canUndo: state.decisions.length > 0,
    startedAt: state.session.startedAt,
    // HR's note so far (§L31), until this sitting changes it.
    note: note?.text ?? card?.review.note ?? '',
    isNoteUnsaved: note?.isDirty === true,
    decide,
    undo,
    setNote,
    saveNote,
    changeSort,
    finish,
  };
}

function initialState(start: SortingStartResponse, session: SortingSessionView): State {
  return {
    cards: start.cards,
    session,
    progress: start.progress,
    pools: start.pools,
    pending: [],
    decisions: [],
    notes: {},
    isOffline: false,
    mayRefill: true,
    isRefilling: false,
    endedMessage: null,
    shownAt: Date.now(),
  };
}

function statusOf(state: State): SortingStatus {
  if (state.endedMessage !== null) return 'ended';
  if (state.cards.length > 0) return 'active';
  const isStillDeciding = state.pending.some((op) => op.kind === 'decision' || op.kind === 'undo');
  if (isStillDeciding || state.isRefilling || state.mayRefill) return 'loading';
  return 'exhausted';
}
